import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, ArrowLeft } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { Panel, Empty } from '../../components/programme/insights/HeadlineCards';
import { API, StatGrid, LoginChart, DataTable, fmtDate, pct, downloadXlsx } from '../../components/analytics/AnalyticsBits';

const statsFor = (h) => [
  { key: 'members', label: 'Members', value: h.members },
  { key: 'avg-progress', label: 'Average progress', value: `${h.avg_progress}%`, tone: 'text-sky-300' },
  { key: 'active-7d', label: 'Logged in this week', value: h.active_7d, sub: `${h.logins_30d} logins in 30 days` },
  { key: 'at-risk', label: 'Falling behind', value: h.at_risk, tone: h.at_risk ? 'text-rose-300' : 'text-white' },
  { key: 'quiz-attempts', label: 'Quiz attempts', value: h.quiz_attempts },
  { key: 'quiz-avg', label: 'Average quiz score', value: pct(h.quiz_avg), tone: 'text-amber-300' },
  { key: 'pass-rate', label: 'Quiz pass rate', value: pct(h.quiz_pass_rate) },
  { key: 'badges', label: 'Badges earned', value: h.badges },
];

const ProgressSpread = ({ buckets }) => (
  <Panel title="Progress spread" subtitle="How many members sit in each progress band" testId="cohort-progress-chart">
    {!buckets.some((b) => b.count) ? <Empty>No members yet.</Empty> : (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={buckets} margin={{ left: -24, right: 8 }}>
          <CartesianGrid vertical={false} stroke="#27272a" />
          <XAxis dataKey="range" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 12, fontSize: 12 }} formatter={(v) => [v, 'Members']} />
          <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </Panel>
);

export default function CohortAnalyticsPage() {
  const { cohortId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/analytics/cohorts/${cohortId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || 'Could not load analytics'));
  }, [cohortId, token]);

  const name = d?.cohort.name || 'Cohort';
  return (
    <Layout pageTitle={`${name} — Cohort analytics`} breadcrumbItems={[{ label: 'Admin', href: '/admin?tab=cohorts' }, { label: 'Cohorts', href: '/admin?tab=cohorts' }]}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="cohort-analytics-page">
        <button onClick={() => navigate('/admin?tab=cohorts')} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="cohort-analytics-back-btn"><ArrowLeft className="h-4 w-4" />Cohorts</button>
        {error && <p className="text-rose-300" data-testid="analytics-error">{error}</p>}
        {!d && !error && <div className="h-64 rounded-2xl bg-zinc-900 animate-pulse" />}
        {d && (<>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white" data-testid="cohort-analytics-name">{name}</h1>
              <p className="text-zinc-400 text-sm mt-1">Updated {fmtDate(d.generated_at, true)}</p>
            </div>
            <Button onClick={() => downloadXlsx(`${API}/admin/analytics/cohorts/${cohortId}/export`, token, `${name}_analytics.xlsx`)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="export-cohort-analytics-btn">
              <Download className="h-4 w-4 mr-2" />Export to Excel
            </Button>
          </div>
          <StatGrid stats={statsFor(d.headline)} />
          <div className="grid lg:grid-cols-2 gap-6">
            <LoginChart data={d.logins_daily} title="Cohort logins — last 30 days" />
            <ProgressSpread buckets={d.progress_buckets} />
          </div>
          <Panel title="Linked programmes" testId="cohort-programmes">
            <DataTable testId="cohort-programmes-table" empty="No programmes linked to this cohort yet." rows={d.programmes.map((p) => ({ ...p, key: p.programme_id }))} columns={[
              { label: 'Programme', render: (r) => <span className="text-white">{r.name}</span> },
              { label: 'Enrolled', render: (r) => r.enrolled },
              { label: 'Not enrolled', render: (r) => r.not_enrolled },
              { label: 'Avg progress', render: (r) => `${r.avg_progress}%` },
              { label: 'Completed', render: (r) => r.completed },
            ]} />
          </Panel>
          <Panel title="Members" subtitle="Lowest progress first · click a learner for their full profile" testId="cohort-members">
            <DataTable testId="cohort-members-table" empty="No learners in this cohort yet. Assign them in Admin → Users."
              onRowClick={(r) => navigate(`/admin/users/${r.user_id}/analytics`)}
              rows={d.members.map((m) => ({ ...m, key: m.user_id, testId: `cohort-member-row-${m.user_id}` }))} columns={[
                { label: 'Learner', render: (r) => <div><p className="text-white">{r.name}</p><p className="text-xs text-zinc-500">{r.email}</p></div> },
                { label: 'Progress', render: (r) => `${r.avg_programme_progress}%` },
                { label: 'Logins (30d)', render: (r) => r.logins_30d },
                { label: 'Last login', render: (r) => (r.last_login ? fmtDate(r.last_login) : 'Never') },
                { label: 'Quiz attempts', render: (r) => r.quiz_attempts },
                { label: 'Quiz avg', render: (r) => pct(r.quiz_avg) },
                { label: 'Courses done', render: (r) => `${r.courses_completed}/${r.courses}` },
                { label: 'Status', render: (r) => (r.at_risk ? <span className="text-rose-300">Falling behind</span> : <span className="text-emerald-300">On track</span>) },
              ]} />
          </Panel>
        </>)}
      </div>
    </Layout>
  );
}
